import { connectBluetoothPrinter } from './bluetoothPrinter';

export interface ZplLabelItem {
  name: string;
  brand?: string;
  rate: number;
  unit?: string;
  barcode: string; // raw barcode string, NOT an image — ZPL prints barcodes natively
  qty: number;
}

interface ZplLabelFields {
  name: boolean;
  brand: boolean;
  price: boolean;
  unit: boolean;
  barcodeNumber: boolean; // shows/hides ZPL's own human-readable line under the bars
}

interface ZplLabelOpts {
  currSym?: string;
  widthDots?: number;  // label width in dots at 203dpi — 400 ≈ 50mm, 609 ≈ 76mm
  heightDots?: number; // label height in dots — 240 ≈ 30mm, 300 ≈ 38mm
  fields?: ZplLabelFields; // which parts to include — defaults to everything on
}

const DEFAULT_ZPL_FIELDS: ZplLabelFields = { name: true, brand: true, price: true, unit: true, barcodeNumber: true };

// Escapes characters ZPL's ^FD (field data) block treats specially.
function zplEscape(text: string): string {
  return text.replace(/\^/g, '').replace(/~/g, '');
}

// Builds ZPL for ONE label. Uses ^BC (Code128), the same symbology JsBarcode
// was rendering as an image for the ESC/POS path — but here the PRINTER
// itself draws the bars from the raw digits/text, no raster image needed.
function buildZplLabel(item: ZplLabelItem, opts: ZplLabelOpts): string {
  const w = opts.widthDots || 400;
  const h = opts.heightDots || 240;
  const sym = opts.currSym || '₹';
  const fields = opts.fields || DEFAULT_ZPL_FIELDS;

  const lines: string[] = ['^XA', `^PW${w}`, `^LL${h}`];
  let y = 10;

  if (fields.name && item.name) {
    const name = zplEscape(item.name).slice(0, 40);
    lines.push('^CF0,28', `^FO10,${y}^FD${name}^FS`);
    y += 34;
  }

  if (fields.brand && item.brand) {
    const brand = zplEscape(item.brand).slice(0, 40);
    lines.push('^CF0,20', `^FO10,${y}^FD${brand}^FS`);
    y += 26;
  }

  // ^BCN's Y/N third field controls whether ZPL prints its own
  // human-readable line under the bars — that's the barcodeNumber toggle.
  lines.push('^BY2,2,60', '^CF0,34', `^FO10,${y}^BCN,60,${fields.barcodeNumber ? 'Y' : 'N'},N,N^FD${zplEscape(item.barcode)}^FS`);
  y += fields.barcodeNumber ? 90 : 70;

  const priceLine = fields.price && item.rate > 0
    ? zplEscape(`${sym}${item.rate.toFixed(2)}${fields.unit && item.unit ? ' / ' + item.unit : ''}`)
    : (fields.unit && item.unit ? zplEscape(`per ${item.unit}`) : '');

  if (priceLine) {
    lines.push('^CF0,32', `^FO10,${Math.min(y, h - 40)}^FD${priceLine}^FS`);
  }

  lines.push('^XZ');
  return lines.join('\n');
}

// Connects (or reuses pairing) and streams every selected product, expanded
// by quantity, as ZPL labels. ZPL is plain ASCII text — no chunk-delay
// dance needed the way raster ESC/POS bitmaps require, but we chunk writes
// anyway since many BLE characteristics still cap per-write size.
export async function printBarcodeLabelsZPL(
  items: ZplLabelItem[],
  opts: ZplLabelOpts = {}
): Promise<boolean> {
  if (items.length === 0) return false;
  try {
    const characteristic = await connectBluetoothPrinter();
    if (!characteristic) return false;

    let zpl = '';
    for (const item of items) {
      const label = buildZplLabel(item, opts);
      for (let i = 0; i < item.qty; i++) zpl += label + '\n';
    }

    const bytes = Array.from(new TextEncoder().encode(zpl));
    const chunk = 100; // ZPL is plain text — safe to send larger chunks than raster data
for (let i = 0; i < bytes.length; i += chunk) {
  await characteristic.writeValue(new Uint8Array(bytes.slice(i, i + chunk)));
  await new Promise(r => setTimeout(r, 20));
}
    return true;
  } catch (e) {
    console.error('ZPL label print failed:', e);
    return false;
  }
}